import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

export default function HeroSection() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center overflow-hidden"
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/e60a139aceeea1da5f1e54f7a90b1120.jpg"
          alt="Fondo dramático"
          className="w-full h-full object-cover object-center"
        />
        {/* Multi-layer overlay for depth */}
        <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, rgba(0,10,30,0.92) 0%, rgba(0,29,80,0.80) 50%, rgba(0,10,30,0.88) 100%)" }} />
        {/* Radial glow center */}
        <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 60% 50%, rgba(212,175,55,0.12) 0%, transparent 65%)" }} />
        {/* Bottom fade */}
        <div className="absolute bottom-0 left-0 w-full h-40" style={{ background: "linear-gradient(to top, rgba(0,10,30,0.9), transparent)" }} />
      </div>

      {/* Animated gold particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(18)].map((_, i) => (
          <div
            key={i}
            className="absolute rounded-full"
            style={{
              width: `${Math.random() * 3 + 1}px`,
              height: `${Math.random() * 3 + 1}px`,
              background: "#D4AF37",
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              opacity: Math.random() * 0.5 + 0.1,
              animation: `float-particle ${Math.random() * 8 + 6}s ease-in-out infinite`,
              animationDelay: `${Math.random() * 5}s`,
            }}
          />
        ))}
      </div>

      {/* Gold accent lines */}
      <div className="absolute top-0 left-0 w-full h-[2px]" style={{ background: "linear-gradient(90deg, transparent 0%, #D4AF37 30%, #F0D060 50%, #D4AF37 70%, transparent 100%)" }} />
      <div className="absolute bottom-0 left-0 w-full h-[2px]" style={{ background: "linear-gradient(90deg, transparent 0%, #D4AF37 30%, #F0D060 50%, #D4AF37 70%, transparent 100%)" }} />

      {/* Vertical gold accent left */}
      <div className="absolute left-0 top-0 h-full w-[3px]" style={{ background: "linear-gradient(to bottom, transparent, #D4AF37 30%, #D4AF37 70%, transparent)" }} />

      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-16 py-24 md:py-32">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">

          {/* Book Cover */}
          <div
            className={`flex-shrink-0 transition-all duration-1000 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}
            style={{ transitionDelay: "0.2s" }}
          >
            <div className="relative">
              {/* Outer glow ring */}
              <div
                className="absolute rounded-2xl"
                style={{
                  inset: "-12px",
                  background: "linear-gradient(135deg, rgba(212,175,55,0.4), rgba(212,175,55,0.05), rgba(212,175,55,0.3))",
                  borderRadius: "20px",
                  filter: "blur(8px)",
                }}
              />
              {/* Gold border frame */}
              <div
                className="absolute rounded-xl"
                style={{
                  inset: "-3px",
                  background: "linear-gradient(135deg, #D4AF37, #8B6914, #D4AF37, #F0D060)",
                  borderRadius: "14px",
                }}
              />
              {/* Book */}
              <div
                className="relative rounded-xl overflow-hidden"
                style={{
                  transform: "rotate(-2deg) perspective(800px) rotateY(5deg)",
                  boxShadow: "0 50px 100px rgba(0,0,0,0.8), 30px 20px 60px rgba(0,0,0,0.5)",
                  width: "280px",
                  height: "420px",
                  transition: "transform 0.4s ease",
                }}
                onMouseEnter={e => {
                  (e.currentTarget as HTMLDivElement).style.transform = "rotate(0deg) perspective(800px) rotateY(0deg) scale(1.03)";
                }}
                onMouseLeave={e => {
                  (e.currentTarget as HTMLDivElement).style.transform = "rotate(-2deg) perspective(800px) rotateY(5deg)";
                }}
              >
                <img
                  src="https://public.readdy.ai/ai/img_res/0b226bd2-7f0a-4cc4-9c0f-1f1673ca9666.png"
                  alt="El Secuestro de Lucho - Portada del libro"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-white/15 via-transparent to-black/20" />
              </div>

              {/* Badge */}
              <div
                className="absolute -top-4 -right-4 w-20 h-20 rounded-full flex flex-col items-center justify-center text-center"
                style={{
                  background: "linear-gradient(135deg, #D4AF37, #F0D060)",
                  boxShadow: "0 8px 24px rgba(212,175,55,0.5)",
                  transform: "rotate(12deg)",
                }}
              >
                <p className="font-bold text-xs leading-tight" style={{ color: "#002D62", fontFamily: "'Playfair Display', serif" }}>
                  HISTORIA<br />REAL
                </p>
              </div>
            </div>
          </div>

          {/* Text Content */}
          <div
            className={`flex-1 text-center lg:text-left transition-all duration-1000 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}
            style={{ transitionDelay: "0.5s" }}
          >
            {/* Pre-title with decorative line */}
            <div className="flex items-center justify-center lg:justify-start gap-4 mb-6">
              <div className="h-[1px] w-12" style={{ background: "#D4AF37" }} />
              <p
                className="text-xs font-semibold tracking-widest uppercase"
                style={{ color: "#D4AF37", letterSpacing: "4px" }}
              >
                Testimonio Real &bull; Supervivencia &bull; Fe
              </p>
              <div className="h-[1px] w-12" style={{ background: "#D4AF37" }} />
            </div>

            {/* Main Title */}
            <h1
              className="font-bold leading-tight mb-4"
              style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: "clamp(2.5rem, 5.5vw, 4.5rem)",
                color: "#FFFFFF",
                lineHeight: 1.1,
                textShadow: "0 4px 30px rgba(0,0,0,0.5)",
              }}
            >
              EL SECUESTRO<br />
              <span
                style={{
                  color: "#D4AF37",
                  WebkitTextStroke: "1px rgba(212,175,55,0.3)",
                  filter: "drop-shadow(0 0 20px rgba(212,175,55,0.4))",
                }}
              >
                DE LUCHO
              </span>
            </h1>

            {/* Divider ornament */}
            <div className="flex items-center justify-center lg:justify-start gap-3 mb-5">
              <div className="h-[1px] flex-1 max-w-[60px]" style={{ background: "rgba(212,175,55,0.4)" }} />
              <i className="ri-star-fill text-xs" style={{ color: "#D4AF37" }} />
              <div className="h-[1px] flex-1 max-w-[60px]" style={{ background: "rgba(212,175,55,0.4)" }} />
            </div>

            {/* Question */}
            <p
              className="font-medium mb-6"
              style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: "clamp(1.1rem, 2.5vw, 1.5rem)",
                color: "rgba(255,255,255,0.9)",
                fontStyle: "italic",
              }}
            >
              ¿Planificación o Equivocación?
            </p>

            {/* Subtitle */}
            <p
              className="mb-10 max-w-xl mx-auto lg:mx-0"
              style={{
                fontFamily: "'Inter', sans-serif",
                fontSize: "clamp(0.95rem, 1.8vw, 1.1rem)",
                color: "rgba(255,255,255,0.72)",
                lineHeight: 1.8,
              }}
            >
              El testimonio real de un hombre de Leyes, Tierras y Ejecutivo de Seguros que enfrentó{" "}
              <strong style={{ color: "#D4AF37" }}>67 días de oscuridad</strong>{" "}
              para hallar una luz inquebrantable.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-center lg:items-start gap-4">
              <a
                href="https://books2read.com/el-secuestro-de-lucho"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 font-bold transition-all duration-300 whitespace-nowrap cursor-pointer"
                style={{
                  background: "linear-gradient(135deg, #D4AF37 0%, #F0D060 50%, #D4AF37 100%)",
                  color: "#001A3D",
                  padding: "18px 44px",
                  borderRadius: "50px",
                  fontSize: "1rem",
                  boxShadow: "0 8px 32px rgba(212,175,55,0.45), 0 0 0 1px rgba(212,175,55,0.3)",
                  letterSpacing: "0.5px",
                }}
                onMouseEnter={e => {
                  (e.currentTarget as HTMLAnchorElement).style.transform = "translateY(-4px) scale(1.02)";
                  (e.currentTarget as HTMLAnchorElement).style.boxShadow = "0 20px 50px rgba(212,175,55,0.6), 0 0 0 1px rgba(212,175,55,0.5)";
                }}
                onMouseLeave={e => {
                  (e.currentTarget as HTMLAnchorElement).style.transform = "translateY(0) scale(1)";
                  (e.currentTarget as HTMLAnchorElement).style.boxShadow = "0 8px 32px rgba(212,175,55,0.45), 0 0 0 1px rgba(212,175,55,0.3)";
                }}
              >
                <i className="ri-amazon-fill text-xl" />
                Adquirir el libro en Amazon
              </a>

              <a
                href="#historia"
                className="inline-flex items-center gap-2 font-medium transition-all duration-300 whitespace-nowrap cursor-pointer"
                style={{
                  color: "rgba(255,255,255,0.8)",
                  padding: "18px 32px",
                  borderRadius: "50px",
                  fontSize: "0.95rem",
                  border: "1px solid rgba(255,255,255,0.2)",
                }}
                onMouseEnter={e => {
                  (e.currentTarget as HTMLAnchorElement).style.borderColor = "rgba(212,175,55,0.5)";
                  (e.currentTarget as HTMLAnchorElement).style.color = "#D4AF37";
                }}
                onMouseLeave={e => {
                  (e.currentTarget as HTMLAnchorElement).style.borderColor = "rgba(255,255,255,0.2)";
                  (e.currentTarget as HTMLAnchorElement).style.color = "rgba(255,255,255,0.8)";
                }}
              >
                Conocer la historia
                <i className="ri-arrow-right-line" />
              </a>

              <Link
                to="/libro"
                className="inline-flex items-center gap-2 font-medium transition-all duration-300 whitespace-nowrap cursor-pointer"
                style={{
                  color: "#D4AF37",
                  padding: "18px 32px",
                  borderRadius: "50px",
                  fontSize: "0.95rem",
                  border: "1px solid rgba(212,175,55,0.4)",
                  background: "rgba(212,175,55,0.08)",
                }}
                onMouseEnter={e => {
                  (e.currentTarget as HTMLAnchorElement).style.background = "rgba(212,175,55,0.18)";
                  (e.currentTarget as HTMLAnchorElement).style.borderColor = "#D4AF37";
                  (e.currentTarget as HTMLAnchorElement).style.transform = "translateY(-2px)";
                }}
                onMouseLeave={e => {
                  (e.currentTarget as HTMLAnchorElement).style.background = "rgba(212,175,55,0.08)";
                  (e.currentTarget as HTMLAnchorElement).style.borderColor = "rgba(212,175,55,0.4)";
                  (e.currentTarget as HTMLAnchorElement).style.transform = "translateY(0)";
                }}
              >
                <i className="ri-book-open-line" />
                Ver el libro
              </Link>
            </div>

            {/* Book summary card */}
            <div
              className="mt-10 rounded-2xl p-6 max-w-xl mx-auto lg:mx-0"
              style={{
                background: "rgba(255,255,255,0.05)",
                border: "1px solid rgba(212,175,55,0.2)",
                backdropFilter: "blur(8px)",
              }}
            >
              {/* Header de la card */}
              <div className="flex items-center gap-3 mb-4">
                <div
                  className="w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0"
                  style={{ background: "linear-gradient(135deg, #D4AF37, #F0D060)" }}
                >
                  <i className="ri-book-2-line text-sm" style={{ color: "#002D62" }} />
                </div>
                <p className="text-xs font-bold tracking-widest uppercase" style={{ color: "#D4AF37" }}>
                  Sobre el libro
                </p>
              </div>

              {/* Resumen */}
              <p className="text-sm leading-relaxed mb-4" style={{ color: "rgba(255,255,255,0.72)" }}>
                En <strong style={{ color: "rgba(255,255,255,0.9)" }}>"EL SECUESTRO DE LUCHO"</strong>, Carlos Alberto Díaz Martínez narra el testimonio crudo de su cautiverio a manos de la guerrilla. Un{" "}
                <strong style={{ color: "#D4AF37" }}>thriller psicológico y espiritual de la vida real</strong>{" "}
                que te mantendrá en vilo desde la primera página.
              </p>

              {/* Puntos clave */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
                {[
                  { icon: "ri-spy-line", text: "El misterio de una identidad" },
                  { icon: "ri-mental-health-line", text: "La batalla en la oscuridad" },
                  { icon: "ri-heart-pulse-line", text: "El calvario invisible" },
                  { icon: "ri-sun-line", text: "Un mensaje de resiliencia" },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      <i className={`${item.icon} text-xs`} style={{ color: "#D4AF37" }} />
                    </div>
                    <span className="text-xs" style={{ color: "rgba(255,255,255,0.6)" }}>{item.text}</span>
                  </div>
                ))}
              </div>

              {/* Link a la página del libro */}
              <Link
                to="/libro"
                className="inline-flex items-center gap-2 text-xs font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap"
                style={{ color: "#D4AF37" }}
                onMouseEnter={e => (e.currentTarget as HTMLAnchorElement).style.gap = "10px"}
                onMouseLeave={e => (e.currentTarget as HTMLAnchorElement).style.gap = "8px"}
              >
                Leer el resumen completo
                <i className="ri-arrow-right-line" />
              </Link>
            </div>

          </div>
        </div>
      </div>

      {/* Políticas link */}
      <div className="absolute bottom-8 right-8">
        <Link
          to="/politicas"
          className="inline-flex items-center gap-1.5 text-xs transition-all duration-200 cursor-pointer whitespace-nowrap"
          style={{ color: "rgba(255,255,255,0.35)" }}
          onMouseEnter={e => (e.currentTarget as HTMLAnchorElement).style.color = "#D4AF37"}
          onMouseLeave={e => (e.currentTarget as HTMLAnchorElement).style.color = "rgba(255,255,255,0.35)"}
        >
          <i className="ri-shield-check-line text-xs" />
          Políticas y Privacidad
        </Link>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2">
        <p className="text-xs tracking-widest" style={{ color: "rgba(255,255,255,0.35)", letterSpacing: "3px" }}>DESCUBRIR</p>
        <div
          className="w-6 h-10 rounded-full flex items-start justify-center pt-2"
          style={{ border: "1px solid rgba(255,255,255,0.2)" }}
        >
          <div
            className="w-1 h-2 rounded-full"
            style={{
              background: "#D4AF37",
              animation: "scroll-dot 2s ease-in-out infinite",
            }}
          />
        </div>
      </div>

      <style>{`
        @keyframes float-particle {
          0%, 100% { transform: translateY(0px) translateX(0px); opacity: 0.2; }
          33% { transform: translateY(-20px) translateX(10px); opacity: 0.6; }
          66% { transform: translateY(-10px) translateX(-8px); opacity: 0.3; }
        }
        @keyframes scroll-dot {
          0%, 100% { transform: translateY(0); opacity: 1; }
          50% { transform: translateY(12px); opacity: 0.3; }
        }
      `}</style>
    </section>
  );
}
